/**
 * @namespace source.item
 */
import { ContentType } from '../../constants/ContentType';
/**
 * @typedef Item
 * @type {object}
 * @property {string} id
 * @property {string} folderId
 * @property {string} name
 * @property {string} mediaType
 * @property {string} fileUrl
 * @property {string} thumbnailUrl
 * @property {string} originalUrl
 * @property {string} previewUrl
 * @property {date} createdAt
 * @property {array} tags
 * @property {number} width
 * @property {number} height
 * @property {object} fileInput
 * @property {object} fileOutput
 * @property {string} transcodingStatus
 * @property {number} duration
 */
import { TranscodingStatuses } from '../../constants/transcoding-statuses';
import { UploadEvent } from '../../constants/upload-event';
import { createUploadMarks, sendUploadTimingsBiEvent } from '../../services/bi/benchmarks';
import { getUoUParams } from '../../services/bi/uou';
import { mark, now } from '../../services/performance';
import { extractCancelAndProgressConfig } from '../../utils/http';
import { toErrorObject } from '../../utils/mappers';
import { get, normalizeUri } from '../../utils/utils';
import { toItem } from './mappers/item';
import { toError } from './parsers/error';
import { ACL } from './settings';
import { validateNewProps } from './validators/new-props';
import { initTusUploader } from '../../utils/tusProtocol/initTusUploader';
import { DUPLEXER_EVENTS } from '../../services/socket/createDuplexerChannel';
const POLLING_TIMEOUT = 15 * 1000;
const TIMEOUT_BEFORE_REJECT = 10 * 60 * 1000;
const MIN_FONT_FILE_SIZE = 4096;
export function initItem(settings, http, channel, pollItem, resumableUpload) {
  let isPromiseResolved;
  let rejectTimeoutId;
  let funcToUnsubscribe;
  const defaultAcl = settings.namespace ? ACL.PRVATE : ACL.PUBLIC;
  function mapItem(res, thumbnailsSizes) {
    res.data = toItem(res.data, settings, thumbnailsSizes);
    return res;
  }
  function cleanUp() {
    isPromiseResolved = true;
    clearTimeout(rejectTimeoutId);
    channel.off(DUPLEXER_EVENTS.STATUS_UPDATE, funcToUnsubscribe);
  }
  function resolveItemIdFromSocketMessage(itemIdFromResponse, resolve) {
    return function (message) {
      const itemId = get(message, 'file.file_name');
      if (get(message, 'file.op_status') === TranscodingStatuses.READY && itemId === itemIdFromResponse) {
        resolve(itemId);
      }
    };
  }
  function updateCancelAndProgressConfigForTus(cancelAndProgressConfig, tusProtocol) {
    cancelAndProgressConfig.tusOptions.abort = tusProtocol.abort;
    cancelAndProgressConfig.tusOptions.isResumable = tusProtocol.getIsResumable;
    cancelAndProgressConfig.tusOptions.resume = tusProtocol.resume;
    cancelAndProgressConfig.tusOptions.tryToResume = tusProtocol.tryToResume;
    cancelAndProgressConfig.tusOptions.shouldTryToResume = tusProtocol.getShouldTryToResume;
  }
  async function tusUpload(uploadUrl, data, options, cancelAndProgressConfig, handleResponse) {
    const tusProtocol = initTusUploader(uploadUrl, data, options, cancelAndProgressConfig, handleResponse, http);
    updateCancelAndProgressConfigForTus(cancelAndProgressConfig, tusProtocol);
    return tusProtocol.upload();
  }
  function checkItemStatus(itemId, resolve) {
    setTimeout(function () {
      if (isPromiseResolved) {
        return;
      }
      getRequest(itemId).then(function (response) {
        if (get(response, 'data.transcodingStatus') !== TranscodingStatuses.READY) {
          return Promise.reject();
        }
        resolve(itemId);
      }).catch(function () {
        checkItemStatus(itemId, resolve);
      });
    }, POLLING_TIMEOUT);
  }
  function resolveItemIdFromResponse(itemId) {
    return new Promise(function (resolve, reject) {
      if (!itemId) {
        reject();
        return;
      }
      checkItemStatus(itemId, resolve);
    });
  }
  function waitForItemFromStorage(response) {
    isPromiseResolved = false;
    const itemId = get(response, 'data.payload.file_name') || get(response, 'data.file_name');
    const resolveItemIdFromSocket = new Promise(function (resolve) {
      funcToUnsubscribe = resolveItemIdFromSocketMessage(itemId, resolve);
      channel.on(DUPLEXER_EVENTS.STATUS_UPDATE, funcToUnsubscribe);
    });
    const rejectNotReadyItem = new Promise(function (resolve, reject) {
      rejectTimeoutId = setTimeout(function () {
        reject({
          status: 406,
          data: {
            message: 'Not ready item'
          }
        });
      }, TIMEOUT_BEFORE_REJECT);
    });
    return Promise.race([resolveItemIdFromSocket, resolveItemIdFromResponse(itemId), rejectNotReadyItem]).then(function (itemId) {
      cleanUp();
      return Promise.resolve(itemId);
    }).catch(function (error) {
      cleanUp();
      return Promise.reject(error);
    });
  }
  function getUploadUrl(fileSource, cancelAndProgressConfig, isTus) {
    const {
      file,
      mediaType,
      acl = defaultAcl,
      tags
    } = fileSource;
    const url = `${settings.apiUrl}/v3/files/upload/url`;
    const data = {
      tus: isTus,
      // we just need an extension here
      file_name: file.name.toLowerCase(),
      // file_name: '_.' + file.name.split('.').pop().toLowerCase(),
      file_size: file.size,
      content_type: file.type,
      media_type: mediaType,
      acl,
      tags
    };
    if (settings.namespace) {
      data.ns = settings.namespace;
    }
    const postOptions = {
      withCredentials: true,
      headers: {
        'Content-Type': ContentType.UrlEncoded
      }
    };
    cancelAndProgressConfig = extractCancelAndProgressConfig(cancelAndProgressConfig);
    return http.post(url, data, null, postOptions, cancelAndProgressConfig);
  }
  function uploadByUrl(fileSource, options, cancelAndProgressConfig, skipWaitingItemFromStorage) {
    if (cancelAndProgressConfig === void 0) {
      cancelAndProgressConfig = {};
    }
    if (skipWaitingItemFromStorage === void 0) {
      skipWaitingItemFromStorage = false;
    }
    options = options || {};
    const data = {
      url: normalizeUri(fileSource.url),
      media_type: fileSource.mediaType,
      acl: fileSource.acl || defaultAcl,
      name: fileSource.name || 'Untitled'
    };
    if (fileSource.language) {
      data.language = fileSource.language;
    }
    if (fileSource.folderId) {
      data.parent_folder_id = fileSource.folderId;
    }
    if (fileSource.tags && fileSource.tags.length) {
      data.labels = fileSource.tags.join(',');
    }
    if (fileSource.systemTags && fileSource.systemTags.length) {
      data.tags = fileSource.systemTags.join(',');
    }

    // some sources as Google Drive requires auth headers to allow downloading files via url
    if (fileSource.headers) {
      data.headers = JSON.stringify(fileSource.headers);
    }
    if (fileSource.catalogCallbackUrl) {
      data.cat_callback_url = fileSource.catalogCallbackUrl;
    }
    const vision = options.vision || settings.vision;
    if (vision) {
      data.vision = vision.join(',');
    }
    if (settings.namespace) {
      data.ns = settings.namespace;
    }
    const apiUrl = `${settings.apiUrl}/site/media/files/upload/external`;
    if (settings.asyncUploadByUrl || skipWaitingItemFromStorage) {
      const apiUrlAsync = `${apiUrl}/async`;
      const headers = {
        ...options.headers,
        'Content-Type': ContentType.UrlEncoded
      };
      const postOptions = {
        headers
      };
      cancelAndProgressConfig = extractCancelAndProgressConfig(cancelAndProgressConfig);
      return http.post(apiUrlAsync, data, null, postOptions, cancelAndProgressConfig).then(function (response) {
        if (skipWaitingItemFromStorage) {
          pollItem(response.data.payload.file_name);
          response.data = toItem(response.data.payload, settings);
          return response;
        }
        return waitForItemFromStorage(response).then(getRequest);
      }).catch(toError);
    }
    return http.get(apiUrl, data, {
      headers: options.headers || {}
    }, cancelAndProgressConfig).then(res => mapItem(res, options.thumbnails));
  }
  function getUploadResponseHandler(uploadMarks, fileSource, uploadUrl, startProgressMark, endProgressMark, isAsyncFontUpload) {
    return response => {
      mark(uploadMarks.responseEnd);
      const endResponseMark = now();
      // v3 endpoints returns { file } object instead of array
      response.data = response.data.file;
      sendUploadTimingsBiEvent('private-site(item.upload)', {
        response,
        fileSource,
        uploadUrl,
        startProgressMark,
        endProgressMark,
        endResponseMark,
        uploadMarks
      });
      if (isAsyncFontUpload) {
        return waitForItemFromStorage(response).then(getRequest);
      }
      const status = get(response, 'data.op_status');
      if (settings.asyncTranscoding && status && status !== TranscodingStatuses.READY) {
        pollItem(response.data.file_name);
      }
      response.data = toItem(response.data, settings);
      return response;
    };
  }
  function upload(fileSource, options, cancelAndProgressConfig, isTus) {
    if (options === void 0) {
      options = {};
    }
    if (cancelAndProgressConfig === void 0) {
      cancelAndProgressConfig = {};
    }
    options = options || {};
    let responseHandler;
    const data = {
      parent_folder_id: fileSource.folderId || null,
      file: fileSource.file,
      media_type: fileSource.mediaType,
      acl: fileSource.acl || defaultAcl
    };
    if (fileSource.language) {
      data.language = fileSource.language;
    }
    if (fileSource.catalogCallbackUrl) {
      data.cat_callback_url = fileSource.catalogCallbackUrl;
    }
    if (fileSource.encodingOptions) {
      data.encoding_options = JSON.stringify(fileSource.encodingOptions);
    }
    const vision = options.vision || settings.vision;
    if (vision) {
      data.vision = vision.join(',');
    }
    if (settings.namespace) {
      data.ns = settings.namespace;
    }
    if (fileSource.tags && fileSource.tags.length) {
      data.tags = fileSource.tags.join(',');
    }
    if (fileSource.labels && fileSource.labels.length) {
      data.labels = fileSource.labels.join(',');
    }
    const uOuParams = getUoUParams();
    if (uOuParams.bsi) {
      data.bsi = uOuParams.bsi;
    }
    const isAsyncFontUploadAllowed = settings.asyncFontUpload;
    const isUploadFont = data.media_type === 'ufont';
    const fileSize = data.file && data.file.size;
    const isBigFile = fileSize > MIN_FONT_FILE_SIZE;
    const isAsyncFontUpload = isAsyncFontUploadAllowed && isUploadFont && isBigFile;
    let startProgressMark;
    let endProgressMark;
    let uploadUrl;
    const uploadMarks = createUploadMarks();
    mark(uploadMarks.requestStart);
    cancelAndProgressConfig = extractCancelAndProgressConfig(cancelAndProgressConfig);
    const getUploadUrlPromise = options.upload_url && options.upload_token ? Promise.resolve(options) : getUploadUrl(fileSource, cancelAndProgressConfig, isTus).then(response => {
      return response.data;
    });
    return getUploadUrlPromise.then(function (responseData) {
      mark(uploadMarks.urlEnd);
      data.upload_token = responseData.upload_token;
      uploadUrl = responseData.upload_url;
      if (isAsyncFontUpload) {
        data.upload_async = true;
      }
      cancelAndProgressConfig.progressEventEmitter.on(UploadEvent.Upload, progress => {
        if (!startProgressMark) {
          // start progress
          startProgressMark = now();
        }
        if (progress.loaded >= progress.total) {
          // 100% progress
          endProgressMark = now();
          mark(uploadMarks.uploadEnd);
        }
      });
      const uploadSettings = [uploadUrl, data, {
        headers: options.headers || {}
      }, cancelAndProgressConfig];
      responseHandler = getUploadResponseHandler(uploadMarks, fileSource, uploadUrl, startProgressMark, endProgressMark, isAsyncFontUpload);
      return isTus ? tusUpload(...uploadSettings, responseHandler) : http.upload(...uploadSettings);
    }).then(response => responseHandler(response)).catch(toError);
  }
  function uploadResumable(fileSource, options, cancelAndProgressConfig) {
    const data = {
      parent_folder_id: fileSource.folderId || null,
      file: fileSource.file,
      media_type: fileSource.mediaType
    };
    if (settings.namespace) {
      data.ns = settings.namespace;
    }
    cancelAndProgressConfig = extractCancelAndProgressConfig(cancelAndProgressConfig);
    return resumableUpload(data, options, cancelAndProgressConfig);
  }

  /**
   * @summary Returns item
   * @memberof source.item
   * @param {String} itemId
   * @param {Object} [options]
   * @returns {Promise}
   * @fulfill {Item}
   * @reject {Error}
   */
  function getRequest(itemId, options) {
    options = options || {};
    const queryParams = {};
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    return http.get(`${settings.apiUrl}/site/media/files/${itemId}`, queryParams, {
      cache: false,
      headers: options.headers || {}
    }).then(res => mapItem(res, options.thumbnails)).catch(toError);
  }

  /**
   * @summary Item url to download
   * @memberof source.item
   * @param {String} itemId
   * @param {Object} [options]
   * @returns {Promise}
   * @fulfill {String} Url
   * @reject {Error}
   */
  function getDownloadUrl(itemId, options) {
    options = options || {};
    const queryParams = {};
    if (settings.namespace) {
      queryParams.ns = settings.namespace;
    }
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    return http.get(`${settings.apiUrl}/site/media/files/${itemId}/url`, queryParams, {
      cache: false,
      headers: options.headers || {}
    }).then(response => ({
      ...response,
      data: {
        encoding: response.data[0].encoding,
        referenceId: response.data[0].reference_id,
        urlPath: response.data[0].url_path
      }
    }))
    // check if url actually works. If it works - return current response.
    // skip checking for storage.googleapis.com
    .then(response => response.data.urlPath.indexOf('storage.googleapis.com') === -1 ? http.head(response.data.urlPath, null, null, {
      withCredentials: false
    }).then(() => response) : response).catch(toError);
  }

  /**
   * @summary Updates the item with given diff object
   * @memberof source.item
   * @param {Item} item Current item object that should be updated
   * @param {object} newItemProps
   * @param {Object} options
   * @returns {Promise}
   * @fulfill {Item} Modified item object
   * @reject {Error}
   */
  function update(item, newItemProps, options) {
    const arePropsValid = validateNewProps(newItemProps);
    if (arePropsValid.valid === false) {
      return toError(toErrorObject({
        status: -200,
        statusText: 'Internal JS Error',
        data: {
          message: arePropsValid.message
        }
      }));
    }
    const data = {};
    if (newItemProps.name) {
      data.original_file_name = newItemProps.name;
    }
    if (newItemProps.folderId || newItemProps.folderId === null) {
      data.parent_folder_id = newItemProps.folderId;
    }
    if (newItemProps.tags) {
      data.tags = newItemProps.tags.join(',');
    }
    if (newItemProps.labels) {
      data.labels = newItemProps.labels;
    }
    if (settings.namespace) {
      data.ns = settings.namespace;
    }
    options = options || {};
    const url = `${settings.apiUrl}/site/media/files/${item.id}`;
    const postOptions = {
      headers: options.headers || {}
    };
    if (options.siteMediaToken) {
      http.setRequestOptionsOnce({
        params: {
          site_token: options.siteMediaToken
        }
      });
    }
    return http.put(url, data, null, postOptions).then(function (res) {
      res.data = {
        ...item,
        ...newItemProps,
        token: options.siteMediaToken
      };
      return res;
    }).catch(toError);
  }
  return {
    upload,
    uploadResumable,
    uploadByUrl,
    get: getRequest,
    update,
    getDownloadUrl
  };
}